import cx from 'classnames';
import { motion, Variants } from 'framer-motion';
import { IFetchResponse } from 'lib/requests/fetch';
import { useRouter } from 'next/router';
import { ButtonHTMLAttributes, useEffect, useState } from 'react';
import css from './SendReminderPill.module.scss';

interface ISendReminderPillProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  color: 'dark-blue' | 'gray' | 'light-blue' | 'purple' | 'red';
  isDisabled?: boolean;
  onClick?: () => void;
}

export const SendReminderPill = ({
  className,
  color,
  isDisabled,
  onClick,
  ...rest
}: ISendReminderPillProps) => (
  <button
    className={cx(css.container, css[color], className)}
    disabled={isDisabled}
    onClick={onClick}
    {...rest}
  >
    Send Reminder
  </button>
);

enum SendState {
  FAILURE,
  INITIAL,
  SENDING,
  SUCCESS,
}

const VARIANTS: Variants = {
  sending: { transition: { duration: 4 }, width: '100%' },
  initial: { transition: { duration: 0 }, width: 0 },
};

interface ITimedSendReminderPillProps extends ISendReminderPillProps {
  onClick?: () => Promise<IFetchResponse<void>>;
}

export const TimedSendReminderPill = ({
  className,
  color,
  isDisabled,
  onClick,
  ...rest
}: ITimedSendReminderPillProps) => {
  const { query } = useRouter();
  const [sendState, setSendState] = useState<SendState>(SendState.INITIAL);

  const isStateInitial = sendState === SendState.INITIAL;
  const isStateFailure = sendState === SendState.FAILURE;
  const isStateSending = sendState === SendState.SENDING;
  const isStateSuccess = sendState === SendState.SUCCESS;

  // Hacky way to reset the send status, but it works (when the qs changes, the
  // assignment or filters have changed and we need to stop the send).
  useEffect(() => {
    setSendState(SendState.INITIAL);
  }, [query]);

  const handleClick = () => {
    switch (sendState) {
      case SendState.INITIAL:
      case SendState.FAILURE:
        setSendState(SendState.SENDING);
        break;
      case SendState.SENDING:
        setSendState(SendState.INITIAL);
        break;
    }
  };

  const handleAnimationComplete = async () => {
    const res = await onClick();
    if (res.error) {
      setSendState(SendState.FAILURE);
    } else {
      setSendState(SendState.SUCCESS);
    }
  };

  return (
    <button
      className={cx(css.container, css[color], className, {
        [css.sending]: isStateSending,
        [css.success]: isStateSuccess,
      })}
      disabled={isDisabled}
      onClick={handleClick}
      {...rest}
    >
      <p className={cx(css.label, { [css.hidden]: !isStateInitial })}>
        Send Reminder
      </p>
      <p className={cx(css.label, { [css.hidden]: !isStateSending })}>
        Cancel Send...
      </p>
      <p className={cx(css.label, { [css.hidden]: !isStateSuccess })}>
        Reminder Sent
      </p>
      <p className={cx(css.label, { [css.hidden]: !isStateFailure })}>
        Failed. Retry?
      </p>
      <motion.div
        animate={isStateSending ? 'sending' : 'initial'}
        className={cx(css.progress, css[color])}
        initial="initial"
        onAnimationComplete={
          isStateSending ? handleAnimationComplete : undefined
        }
        variants={VARIANTS}
      />
    </button>
  );
};
